import React from 'react';
import styled, { keyframes } from 'styled-components';

// Pulse animation
const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.4); opacity: 0.9; }
`;

// Horizontal movement
const moveHorizontal = keyframes`
  0% { left: 10%; }
  50% { left: 90%; }
  100% { left: 10%; }
`;

// Vertical movement — adjusted for dot size (starts 10px below top, ends 10px above bottom)
const moveVertical = keyframes`
  0% { top: 70px; }              /* 60px line + 10px (half dot) */
  50% { top: calc(100% - 70px); }
  100% { top: 70px; }
`;

const Container = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 120px 24px;
  background-color: #fafafa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 8px 20px;
  background-color: #e5e7eb;
  color: #6b7280;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  border-radius: 9999px;
`;

const TimelineWrapper = styled.div`
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
  min-height: 600px;
`;

const HorizontalLine = styled.div`
  position: absolute;
  top: 58px;
  left: 0;
  right: 0;
  height: 2px;
  background-color: #d1d5db;
  z-index: 0;
`;

const VerticalLine = styled.div`
  position: absolute;
  width: 2px;
  background-color: #d1d5db;
  z-index: 0;

  top: 60px;
  height: 80px;
  left: 50%;
  transform: translateX(-50%);

  @media (min-width: 768px) {
    top: 60px;
    height: calc(100% - 80px);
    
    &.first {
      left: 33.333%;
    }

    &.second {
      left: 66.666%;
    }
  }
`;

const Timeline = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 80px;
  z-index: 1;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    gap: 0;
    padding-top: 140px;
  }
`;

const Stat = styled.div`
  text-align: center;
  position: relative;
  z-index: 1;
  flex: 1;
  padding: 2em;
`;

const IconWrapper = styled.div`
  position: relative;
  display: inline-flex;
  margin-bottom: 32px;
`;

const IconCircle = styled.div`
  width: 80px;
  height: 80px;
  background-color: #111111;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AnimatedDot = styled.div`
  position: absolute;
  width:  16px;
  height: 16px;
  background-color: #bfff00;
  border-radius: 50%;
  box-shadow: 0 0 12px rgba(191, 255, 0, 0.6);
  animation: ${pulse} 2s infinite ease-in-out;
  z-index: 2;
`;

// Horizontal dot — perfectly centered vertically on the line
const MovingDotHorizontal = styled(AnimatedDot)`
  top: 50px;
  left: 10%;
  transform: translateY(-50%); /* Only vertical centering */

  @media (max-width: 767px) {
    left: 50%;
    transform: translate(-50%, -120%);
    animation: ${pulse} 2s infinite ease-in-out;
  }

  @media (min-width: 768px) {
    animation: ${moveHorizontal} 14s infinite ease-in-out, ${pulse} 2s infinite ease-in-out;
  }
`;

// Vertical dots — centered both horizontally and vertically on their lines
const MovingDotVertical1 = styled(AnimatedDot)`
  left: 32.6%;
  top: 70px; /* start offset */
  transform: translate(-50%, -50%);

  @media (max-width: 767px) {
    display: none;
  }

  @media (min-width: 768px) {
    animation: ${moveVertical} 10s infinite ease-in-out 1s, ${pulse} 2s infinite ease-in-out;
  }
`;

const MovingDotVertical2 = styled(AnimatedDot)`
  left: 66%;
  top: 70px;
  transform: translate(-50%, -50%);

  @media (max-width: 767px) {
    display: none;
  }

  @media (min-width: 768px) {
    animation: ${moveVertical} 12s infinite ease-in-out 3s, ${pulse} 2s infinite ease-in-out;
  }
`;



const Number = styled.h3`
  font-size: 72px;
  font-weight: 800;
  color: #111111;
  margin: 0 0 16px 0;
  line-height: 1;
`;

const Text = styled.p`
  font-size: 18px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;

  span {
    color: #111111;
    font-weight: 600;
  }
`;

const Stats = () => {
  return (
    <Container>
      <Header>
        <Badge>Did you know?</Badge>
      </Header>

      <TimelineWrapper>
        <HorizontalLine />
        <VerticalLine className="first" />
        <VerticalLine className="second" />

        <MovingDotHorizontal />
        <MovingDotVertical1 />
        <MovingDotVertical2 />

        <Timeline>
          <Stat>
            <IconWrapper>
              <IconCircle>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </IconCircle>
            </IconWrapper>
            <Number>100+</Number>
            <Text>
              Companies using<br />
              <span>Nexus</span>
            </Text>
          </Stat>

          <Stat>
            <IconWrapper>
              <IconCircle>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </IconCircle>
            </IconWrapper>
            <Number>1k</Number>
            <Text>
              Policies<br />
              using <span>Nexus</span>
            </Text>
          </Stat>

          <Stat>
            <IconWrapper>
              <IconCircle>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </IconCircle>
            </IconWrapper>
            <Number>100</Number>
            <Text>
              Underwriters available<br />
              in <span>Nexus</span>
            </Text>
          </Stat>
        </Timeline>
      </TimelineWrapper>
    </Container>
  );
};

export default Stats;